import React from 'react';
import './index.css';
import {initSentry} from './lib/sentry';
import {createRoot} from 'react-dom/client';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import App from './App';
import {configureQueryClient} from './lib/api/tokenRefresh';
import {configureShortLinkBaseUrl} from '@ruta/shared';
import {registerChunkReloadListener} from './lib/chunkReload';
import {applySignupHandoff} from './lib/signupHandoff';

// Registration hand-offs are settled before anything reads a token or the URL.
applySignupHandoff();

// Initialize Sentry before React renders
initSentry();

// A chunk that fails to load leaves a blank screen, because the failed dynamic
// import rejects above every error boundary. That happens whenever a deploy
// lands while the app is open: the chunk hash the running bundle asks for is
// gone. lazyWithRetry in App.tsx covers the routes; this covers the dynamic
// imports that are not routes, and both share one guard so a single failure
// cannot reload twice (RUT-3216).
registerChunkReloadListener();

// Tell the shared short-link helpers where short links live (RUT-3112). The API
// reads SHORTLINK_BASE_URL from its Lambda environment; a browser bundle has no
// process.env, so the value is handed over explicitly here. Without it the
// message preview would under-report how short a text really ends up.
configureShortLinkBaseUrl(import.meta.env.VITE_SHORTLINK_BASE_URL);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes — prevents refetches on route navigation
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Global error handling
      throwOnError: (error: any) => {
        // Throw authentication errors to be handled by error boundaries
        if (error?.status === 401 || error?.status === 403) {
          return true;
        }
        // Don't throw other errors globally, let components handle them
        return false;
      },
    },
    mutations: {
      retryDelay: 1000,
    },
  },
  // Global error handler for logging
  logger: {
    log: console.log,
    warn: console.warn,
    error: (error) => {
      console.error('TanStack Query Error:', error);
    },
  },
});

// Apply our token refresh configuration
configureQueryClient(queryClient);

createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);
